import { MutableRefObject, RefObject, useCallback, useMemo } from 'react'
import { GridApi, RowDragEndEvent } from 'ag-grid-community'
import { useDragTreeStyle } from '../../../containers/BulkSheetView/hooks/gridEvents/treeRowDrag'
import {
  moveRowsToLastChild,
  slideRowsAfter,
  slideRowsBefore,
} from '../../../containers/BulkSheetView/hooks/actions/moveTreeRows'
import { filterTopLevelRows } from '../../../containers/BulkSheetView/lib/tree'
import { ProgramManagementRow } from '../programManagement'
import { programItemRowService } from '../programItemRowService'

export const useProgramManagementDragAndDrop = (
  ref: RefObject<HTMLDivElement>,
  data: ProgramManagementRow[],
  setDataAfterEditing: (data: ProgramManagementRow[]) => void,
  gridApi: GridApi | undefined,
  updateContext: (partial: Record<string, unknown>) => void,
  refreshAggregation: (rows?: ProgramManagementRow[]) => void,
  contextRef: MutableRefObject<Record<string, unknown>>
) => {
  const acceptChild = useCallback(
    (parent: ProgramManagementRow, child: ProgramManagementRow): boolean =>
      programItemRowService.canAcceptChild(parent.type, child.type),
    []
  )

  const { onRowDragEnter, onRowDragMove, refreshDragStyle } =
    useDragTreeStyle<ProgramManagementRow>(
      ref,
      acceptChild,
      gridApi,
      updateContext
    )

  // ドロップ可否の判定は useDragTreeStyle（onRowDragMove）に委ねる。
  // ハイライト時のみ context に draggableNodeId / onTree がセットされるため、
  // ここではそれを読むだけで重複ロジックを持たない（ProjectPlanNew と同じ共通仕様）。
  const onRowDragEnd = useCallback(
    (event: RowDragEndEvent<ProgramManagementRow>) => {
      const overNode = event.overNode
      const draggableNodeId = contextRef.current.draggableNodeId as
        | string
        | undefined
      const onTree = contextRef.current.onTree === true
      refreshDragStyle()

      // ハイライトされた（=ドロップ可能と判定された）行へのドロップのみ処理する
      if (!overNode?.data || overNode.id !== draggableNodeId) return

      const movingRows = filterTopLevelRows(
        event.nodes
          .map(n => n.data)
          .filter((d): d is ProgramManagementRow => d !== undefined)
      )
      if (movingRows.length === 0) return

      const overData = overNode.data
      let newData: ProgramManagementRow[]

      if (onTree) {
        // 親変更: overNode の最後の子として移動
        newData = moveRowsToLastChild(data, movingRows, overData.uuid)
      } else {
        // 同一レベルでの並び替え
        const overIndex = data.findIndex(r => r.uuid === overData.uuid)
        const movingIndex = data.findIndex(r => r.uuid === movingRows[0].uuid)
        newData =
          movingIndex < overIndex
            ? slideRowsAfter(data, movingRows, overData.uuid)
            : slideRowsBefore(data, movingRows, overData.uuid)
      }

      // 集計値をリフレッシュして更新
      setDataAfterEditing(newData)
      refreshAggregation(newData)
    },
    [
      data,
      setDataAfterEditing,
      refreshDragStyle,
      refreshAggregation,
      contextRef,
    ]
  )

  return useMemo(
    () => ({
      onRowDragEnter,
      onRowDragMove,
      onRowDragEnd,
      refreshDragStyle,
    }),
    [onRowDragEnter, onRowDragMove, onRowDragEnd, refreshDragStyle]
  )
}
